import React, { useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import { injectReducer } from 'store'
import reducer from './store'
import { useDispatch, useSelector } from 'react-redux'
import { getData, setTableData } from './store/dataSlice'
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi'
import Tooltip from 'components/Ui/Tooltip'
import { ConfirmationModal, Table } from 'components/Ui'
import { deleteApiRecord } from 'services/RateCardService'
import { Success } from 'utils/alerts'
import useAuth from 'utils/hooks/useAuth'
import dayjs from 'dayjs'
injectReducer('rateCard', reducer)

export default function RateCard() {
    const dispatch = useDispatch()
    const { checkPermission } = useAuth()
    const { data, tableData, loading } = useSelector(
        (state) => state.rateCard.data,
    )

    useEffect(() => {
        fetchData()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        dispatch,
        tableData.sort,
        tableData.query,
        tableData.pageIndex,
        tableData.pageSize,
        tableData.selectedTab,
        tableData.checkBox,
        tableData.service,
    ])

    const fetchData = () => dispatch(getData({ ...tableData }))

    const handleDelete = (id) => {
        deleteApiRecord(id)
            .then(() => {
                Success('Deleted Successfully!')
                fetchData()
            })
            .catch((error) => error)
    }

    const columns = useMemo(
        () => [
            {
                field: 'company',
                header: 'Company',
                className: 'min-w-[150px]',
                renderCell: (row) => row?.assignment?.company_name,
            },
            {
                field: 'from_location',
                header: 'From Location',
                className: 'min-w-[150px]',
            },
            {
                field: 'to_location',
                header: 'To Location',
                className: 'min-w-[150px]',
            },
            {
                field: 'cube',
                header: 'Cube',
                className: 'min-w-[150px]',
            },
            {
                field: 'basic',
                header: 'Basic',
                className: 'min-w-[150px]',
            },
            {
                field: 'minimum',
                header: 'From Minimum',
                className: 'min-w-[150px]',
            },
            {
                field: 'rate',
                header: 'Rate',
                className: 'min-w-[150px]',
            },
            {
                field: 'start_date',
                header: 'From Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.start_date
                        ? dayjs(row?.start_date).format('DD-MMM-YYYY')
                        : '',
            },
            {
                field: 'end_date',
                header: 'To Date',
                className: 'min-w-[150px]',
                renderCell: (row) =>
                    row?.end_date
                        ? dayjs(row?.end_date).format('DD-MMM-YYYY')
                        : '',
            },
            {
                header: 'Action',
                field: 'action',
                className: 'px-4',
                renderCell: (row) => {
                    return (
                        <div className="flex items-center gap-3">
                            {checkPermission('rate-card', 'edit') ? (
                                <Tooltip title={'Edit'}>
                                    <Link
                                        to={`/master-data/rate-card/${row?.id}/edit`}
                                        className="hover:text-primary"
                                    >
                                        <HiOutlinePencil size={20} />
                                    </Link>
                                </Tooltip>
                            ) : null}
                            {checkPermission('rate-card', 'delete') ? (
                                <Tooltip title={'Delete'}>
                                    <ConfirmationModal
                                        message={
                                            'Are you sure you want to delete this record!'
                                        }
                                        onConfirm={() => {
                                            handleDelete(row?.id)
                                        }}
                                    >
                                        <span className="hover:text-danger">
                                            <HiOutlineTrash size={20} />
                                        </span>
                                    </ConfirmationModal>
                                </Tooltip>
                            ) : null}
                        </div>
                    )
                },
            },
        ],
        // eslint-disable-next-line
        [
            tableData.sort,
            tableData.query,
            tableData.pageIndex,
            tableData.pageSize,
            tableData.selectedTab,
            tableData.checkBox,
            tableData.service,
        ],
    )

    return (
        <>
            <div className="flex flex-col gap-1 mb-5 sm:flex-row sm:items-center sm:justify-between">
                <h2 className="font-semibold text-black text-title-md2 dark:text-white">
                    Rate Card
                </h2>
            </div>
            <Table
                loading={loading}
                filter={false}
                servicefilter={true}
                datas={data}
                columns={columns}
                add_new={
                    checkPermission('rate-card', 'create')
                        ? '/master-data/rate-card/create'
                        : ''
                }
                pagingData={tableData}
                onChange={(e) => dispatch(setTableData(e))}
            />
        </>
    )
}
